import 'reflect-metadata';
import { useAppState } from '../../client/providers/AppProvider';
import React, { useMemo } from 'react';
import { NeedsOnboardingInformation, useAuthenticatedQuery, usePageRequireAuth } from '../../client/hooks';
import { DashboardLayout } from '../../components/common/layout/DashboardLayout';
import { RecruitingHub } from '../../components/dashboard/hub/RecruitingHub';
import {
    DashboardDocument,
    DashboardQuery,
    DashboardQueryVariables,
    Espin_Role_Enum,
    RecruitingHubNotificationsFragment,
    RecruitingHubFollowsFragment,
    RecruitingHubMessagesFragment,
    RecruitingHubProfileViewsFragment,
    MessagesCardFragment,
} from '../../generated/spin-graphql';
import { HUBSkeleton } from '../../components/dashboard/hub/HubSkeleton';
import { gql } from '@urql/core';
import { GetStaticProps } from 'next';
import { container } from 'tsyringe';
import { ContentfulService } from '../../server/services/contentful.service';
import { PostSummary } from '../../types/shared.types';
import { filterDuplicates } from '../../shared/shared.utils';
import { useNotificationProvider } from 'client/providers/SpinNotificationProvider';
import { ParentConversations, ParentRecruitingHub } from '../../components/dashboard/hub/ParentRecruitingHub';
import { Conversations } from './messages';

gql`
    query Dashboard(
        $id: String!
        $playerPrimaryGame: espin_game_definition_enum
        $hasPrimary: Boolean!
        $collegeId: uuid
        $highSchoolId: uuid
        $showRegistrations: Boolean = true
        $showCollegeNotifs: Boolean = false
        $showPlayerNotifs: Boolean = false
        $showParentNotifs: Boolean = false
        $showHighSchoolNotifs: Boolean = false
        $playerUserIds: [String!]
    ) {
        admin_recommended_events: espin_event(
            limit: 2
            order_by: { event_start_date: asc }
            where: { admin_recommended: { _eq: true }, status: { _eq: published }, event_end_date: { _gte: "now()" } }
        ) {
            ...RecruitingHubEvents
        }
        recommended_events: espin_event(
            limit: 3
            order_by: { event_start_date: asc }
            where: {
                event_end_date: { _gte: "now()" }
                status: { _eq: published }
                event_games: { game: { game_definition: { _eq: $playerPrimaryGame } } }
            }
        ) @include(if: $hasPrimary) {
            ...RecruitingHubEvents
        }
        any_events: espin_event(
            limit: 3
            order_by: { event_start_date: asc }
            where: { event_end_date: { _gte: "now()" }, status: { _eq: published } }
        ) @skip(if: $hasPrimary) {
            ...RecruitingHubEvents
        }
        registered_events: espin_event(
            limit: 3
            order_by: { event_start_date: asc }
            where: {
                event_end_date: { _gte: "now()" }
                status: { _eq: published }
                event_registrations: { user_id: { _eq: $id } }
            }
        ) @include(if: $showRegistrations) {
            ...RecruitingHubEvents
        }
        college_notifications: espin_notification(
            limit: 6
            order_by: { created_at: desc }
            where: { notification_type: { _neq: conversation_started }, college_id: { _eq: $collegeId } }
        ) @include(if: $showCollegeNotifs) {
            ...RecruitingHubNotifications
        }
        high_school_notifications: espin_notification(
            limit: 6
            order_by: { created_at: desc }
            where: { high_school_id: { _eq: $highSchoolId } }
        ) @include(if: $showHighSchoolNotifs) {
            ...RecruitingHubNotifications
        }
        player_notifications: espin_notification(
            limit: 6
            order_by: { created_at: desc }
            where: { player_user_id: { _eq: $id }, notification_type: { _nin: [interested_in, conversation_started] } }
        ) @include(if: $showPlayerNotifs) {
            ...RecruitingHubNotifications
        }
        parent_notifications: espin_notification(
            where: { player_user_id: { _in: $playerUserIds } }
            order_by: { created_at: desc }
        ) @include(if: $showParentNotifs) {
            ...ParentRecruitingHubNotifications
        }
        parent_children: espin_parent_profile(where: { user_id: { _eq: $id } }) @include(if: $showParentNotifs) {
            ...ParentChildDropdown
        }
        unread_college_messages: espin_message(
            where: {
                _and: {
                    college_id: { _eq: $collegeId }
                    viewed_at: { _is_null: true }
                    message_type: { _eq: player_messages_college }
                }
            }
        ) @include(if: $showCollegeNotifs) {
            ...RecruitingHubMessages
        }
        all_college_messages: espin_message(where: { _and: { college_id: { _eq: $collegeId } } })
        @include(if: $showCollegeNotifs) {
            ...MessagesCard
        }
        college_followers: espin_follow(
            where: { _and: { college_id: { _eq: $collegeId }, follow_type: { _eq: player_follows_college } } }
        ) @include(if: $showCollegeNotifs) {
            ...RecruitingHubFollows
        }
        college_deleted_followers: espin_follow(
            where: {
                _and: {
                    college_id: { _eq: $collegeId }
                    follow_type: { _eq: player_follows_college }
                    player_user: { deleted_at: { _is_null: false } }
                }
            }
        ) @include(if: $showCollegeNotifs) {
            ...RecruitingHubFollows
        }
        college_following: espin_follow(
            where: { _and: { college_id: { _eq: $collegeId }, follow_type: { _eq: college_follows_player } } }
        ) @include(if: $showCollegeNotifs) {
            ...RecruitingHubFollows
        }
        college_profile_views: espin_profile_view(
            where: {
                _and: {
                    college_id: { _eq: $collegeId }
                    profile_view_type: { _in: [parent_views_college, player_views_college] }
                }
            }
        ) @include(if: $showCollegeNotifs) {
            ...RecruitingHubProfileViews
        }
        high_school_followers: espin_follow(
            where: {
                _and: { high_school_id: { _eq: $highSchoolId }, follow_type: { _eq: college_follows_high_school } }
            }
        ) @include(if: $showHighSchoolNotifs) {
            ...RecruitingHubFollows
        }
        high_school_deleted_followers: espin_follow(
            where: {
                _and: {
                    high_school_id: { _eq: $highSchoolId }
                    follow_type: { _eq: college_follows_high_school }
                    college_user: { deleted_at: { _is_null: false } }
                }
            }
        ) @include(if: $showHighSchoolNotifs) {
            ...RecruitingHubFollows
        }
        high_school_following: espin_follow(
            where: {
                _and: { high_school_id: { _eq: $highSchoolId }, follow_type: { _eq: high_school_follows_college } }
            }
        ) @include(if: $showHighSchoolNotifs) {
            ...RecruitingHubFollows
        }
        high_school_profile_views: espin_profile_view(
            where: {
                _and: {
                    high_school_id: { _eq: $highSchoolId }
                    profile_view_type: { _in: [college_views_high_school] }
                }
            }
        ) @include(if: $showHighSchoolNotifs) {
            ...RecruitingHubProfileViews
        }
        unread_player_messages: espin_message(
            where: {
                _and: {
                    player_user_id: { _eq: $id }
                    viewed_at: { _is_null: true }
                    message_type: { _eq: college_messages_player }
                }
            }
        ) @include(if: $showPlayerNotifs) {
            ...RecruitingHubMessages
        }
        all_player_messages: espin_message(where: { _and: { player_user_id: { _eq: $id } } })
        @include(if: $showPlayerNotifs) {
            ...MessagesCard
        }
        player_followers: espin_follow(
            where: { _and: { player_user_id: { _eq: $id }, follow_type: { _eq: college_follows_player } } }
        ) @include(if: $showPlayerNotifs) {
            ...RecruitingHubFollows
        }
        player_deleted_followers: espin_follow(
            where: { _and: { player_user_id: { _eq: $id }, follow_type: { _eq: college_follows_player } } }
        ) @include(if: $showPlayerNotifs) {
            ...RecruitingHubFollows
        }
        player_following: espin_follow(
            where: { _and: { player_user_id: { _eq: $id }, follow_type: { _eq: player_follows_college } } }
        ) @include(if: $showPlayerNotifs) {
            ...RecruitingHubFollows
        }
        player_profile_views: espin_profile_view(
            where: { _and: { player_user_id: { _eq: $id }, profile_view_type: { _eq: college_views_player } } }
        ) @include(if: $showPlayerNotifs) {
            ...RecruitingHubProfileViews
        }
        unread_parent_messages: espin_message(
            where: {
                _and: {
                    player_user_id: { _in: $playerUserIds }
                    viewed_at: { _is_null: true }
                    message_type: { _eq: college_messages_player }
                }
            }
        ) @include(if: $showParentNotifs) {
            ...ParentRecruitingHubMessages
        }
        all_parent_messages: espin_message(where: { _and: { player_user_id: { _in: $playerUserIds } } })
        @include(if: $showParentNotifs) {
            ...MessagesCard
        }
        parent_followers: espin_follow(
            where: { _and: { player_user_id: { _in: $playerUserIds }, follow_type: { _eq: college_follows_player } } }
        ) @include(if: $showParentNotifs) {
            ...RecruitingHubFollows
        }
        parent_deleted_followers: espin_follow(
            where: { _and: { player_user_id: { _in: $playerUserIds }, follow_type: { _eq: college_follows_player } } }
        ) @include(if: $showParentNotifs) {
            ...RecruitingHubFollows
        }
        parent_following: espin_follow(
            where: { _and: { player_user_id: { _in: $playerUserIds }, follow_type: { _eq: player_follows_college } } }
        ) @include(if: $showParentNotifs) {
            ...RecruitingHubFollows
        }
        parent_profile_views: espin_profile_view(
            where: {
                _and: { player_user_id: { _in: $playerUserIds }, profile_view_type: { _eq: college_views_player } }
            }
        ) @include(if: $showParentNotifs) {
            ...RecruitingHubProfileViews
        }
    }
`;

// this is a hybrid page - it's statically generated, but then uses CSR after the fact...
// this is at the moment for colleges  + players + parents!
// todo - would make more sense to break this up.
export default function Dashboard({ articles }: { articles: PostSummary[] }) {
    const { espin_notification: notifications } = useNotificationProvider();
    const { user } = useAppState();

    usePageRequireAuth({});

    const userPrimaryGame = user?.player_profile?.player_games.find((p) => p.primary === true)?.game?.game_definition;
    const userCollege = user?.college_profile?.college?.id;
    const userHighSchool = user?.high_school_profile?.high_school.id;

    const showCollegeNotifs = user?.role === Espin_Role_Enum.EspinCollegeUser;
    const showPlayerNotifs = user?.role === Espin_Role_Enum.EspinPlayerUser;
    const showParentNotifs = user?.role === Espin_Role_Enum.EspinParentUser;
    const showHighSchoolNotifs = user?.role === Espin_Role_Enum.EspinHighschoolUser;
    const playerUserIds = useMemo(() => user?.parent_profile?.child_profiles.map((p) => p.user_id) ?? [], [
        user?.parent_profile?.child_profiles,
    ]);
    const { isReady, data, refetch } = useAuthenticatedQuery<DashboardQuery, DashboardQueryVariables>(
        {
            query: DashboardDocument,
            variables: useMemo(
                () => ({
                    id: user?.id,
                    hasPrimary: !!userPrimaryGame,
                    playerPrimaryGame: userPrimaryGame,
                    showRegistrations: user?.role !== Espin_Role_Enum.EspinCollegeUser,
                    collegeId: userCollege,
                    highSchoolId: userHighSchool,
                    playerUserIds,
                    showCollegeNotifs,
                    showPlayerNotifs,
                    showParentNotifs,
                    showHighSchoolNotifs,
                }),
                [
                    user?.id,
                    user?.role,
                    userPrimaryGame,
                    userCollege,
                    userHighSchool,
                    playerUserIds,
                    showCollegeNotifs,
                    showPlayerNotifs,
                    showParentNotifs,
                    showHighSchoolNotifs,
                ],
            ),
            pause: !!!user,
        },
        ['espin_event', 'espin_notification', 'espin_user'],
    );

    const conversationsByRole: Conversations = useMemo(() => {
        const tempConversations: Conversations = {};
        let conversationsToUse = [];
        if (showPlayerNotifs) {
            conversationsToUse = data?.all_player_messages ?? [];
        } else if (showCollegeNotifs) {
            conversationsToUse = data?.all_college_messages ?? [];
        }
        conversationsToUse.forEach((c: MessagesCardFragment) => {
            tempConversations[user?.role === Espin_Role_Enum.EspinCollegeUser ? c.player_user_id : c.college_id] =
                tempConversations[user?.role === Espin_Role_Enum.EspinCollegeUser ? c.player_user_id : c.college_id] ||
                [];
            tempConversations[user?.role === Espin_Role_Enum.EspinCollegeUser ? c.player_user_id : c.college_id].push(
                c,
            );
        });
        return tempConversations;
    }, [data?.all_college_messages, data?.all_player_messages, user?.role, showCollegeNotifs, showPlayerNotifs]);

    const parentConversations: ParentConversations = useMemo(() => {
        const parentConversations: ParentConversations = {};
        const conversationsToUse = data?.all_parent_messages ?? [];
        conversationsToUse.forEach((c: MessagesCardFragment) => {
            parentConversations[c.player_user_id] = parentConversations[c.player_user_id] || [];
            parentConversations[c.player_user_id][c.college_id] =
                parentConversations[c.player_user_id][c.college_id] || [];
            parentConversations[c.player_user_id][c.college_id].push(c);
        });
        return parentConversations;
    }, [data?.all_parent_messages]);

    const notificationsByRole: RecruitingHubNotificationsFragment[] = useMemo(() => {
        if (showCollegeNotifs) {
            return data?.college_notifications ?? [];
        } else if (showPlayerNotifs) {
            return data?.player_notifications ?? [];
        } else {
            return [];
        }
    }, [data?.college_notifications, data?.player_notifications, showCollegeNotifs, showPlayerNotifs]);

    const followersByRole: RecruitingHubFollowsFragment[] = useMemo(() => {
        if (showCollegeNotifs) {
            return data?.college_followers ?? [];
        } else if (showPlayerNotifs) {
            return data?.player_followers ?? [];
        } else if (showHighSchoolNotifs) {
            return data?.high_school_followers ?? [];
        } else {
            return [];
        }
    }, [
        data?.college_followers,
        data?.player_followers,
        data?.high_school_followers,
        showCollegeNotifs,
        showPlayerNotifs,
        showHighSchoolNotifs,
    ]);

    const deletedFollowersByRole: RecruitingHubFollowsFragment[] = useMemo(() => {
        if (showCollegeNotifs) {
            return data?.college_deleted_followers ?? [];
        } else if (showPlayerNotifs) {
            return data?.player_deleted_followers ?? [];
        } else if (showHighSchoolNotifs) {
            return data?.high_school_deleted_followers ?? [];
        } else {
            return [];
        }
    }, [
        data?.college_deleted_followers,
        data?.player_deleted_followers,
        data?.high_school_deleted_followers,
        showCollegeNotifs,
        showPlayerNotifs,
        showHighSchoolNotifs,
    ]);

    const followingByRole: RecruitingHubFollowsFragment[] = useMemo(() => {
        if (showCollegeNotifs) {
            return data?.college_following ?? [];
        } else if (showPlayerNotifs) {
            return data?.player_following ?? [];
        } else if (showHighSchoolNotifs) {
            return data?.high_school_following ?? [];
        } else {
            return [];
        }
    }, [
        data?.college_following,
        data?.player_following,
        data?.high_school_following,
        showCollegeNotifs,
        showPlayerNotifs,
        showHighSchoolNotifs,
    ]);

    const messagesByRole: RecruitingHubMessagesFragment[] = useMemo(() => {
        if (showCollegeNotifs) {
            return data?.unread_college_messages ?? [];
        } else if (showPlayerNotifs) {
            return data?.unread_player_messages ?? [];
        } else {
            return [];
        }
    }, [data?.unread_college_messages, data?.unread_player_messages, showCollegeNotifs, showPlayerNotifs]);

    const profileViewsByRole: RecruitingHubProfileViewsFragment[] = useMemo(() => {
        if (showCollegeNotifs) {
            return data?.college_profile_views ?? [];
        } else if (showPlayerNotifs) {
            return data?.player_profile_views ?? [];
        } else if (showHighSchoolNotifs) {
            return data?.high_school_profile_views ?? [];
        } else {
            return [];
        }
    }, [
        data?.college_profile_views,
        data?.player_profile_views,
        data?.high_school_profile_views,
        showHighSchoolNotifs,
        showCollegeNotifs,
        showPlayerNotifs,
    ]);

    if (!!!user || !isReady || !!!data) {
        return (
            <DashboardLayout pageTitle="Recruiting Hub">
                <NeedsOnboardingInformation>
                    <HUBSkeleton />
                </NeedsOnboardingInformation>
            </DashboardLayout>
        );
    }

    // build out what events we want
    const {
        admin_recommended_events,
        recommended_events,
        registered_events,
        any_events,
        parent_notifications,
        parent_children,
        parent_followers,
        parent_following,
        parent_deleted_followers,
        unread_parent_messages,
        parent_profile_views,
    } = data;

    const eventsToList = filterDuplicates(
        [
            ...(registered_events || []),
            ...(admin_recommended_events || []),
            ...(recommended_events || []),
            ...(any_events || []),
        ],
        'id',
    );

    let content = (
        <RecruitingHub
            events={eventsToList}
            articles={articles}
            notifications={notifications}
            followers={followersByRole}
            deletedFollowers={deletedFollowersByRole}
            following={followingByRole}
            messages={messagesByRole}
            profileViews={profileViewsByRole}
            refetch={refetch}
            conversations={Object.keys(conversationsByRole).length}
        />
    );

    if (user.role === Espin_Role_Enum.EspinParentUser) {
        content = (
            <ParentRecruitingHub
                events={eventsToList}
                articles={articles}
                notifications={notifications}
                playerInfo={parent_notifications}
                parentChildren={parent_children}
                followers={parent_followers}
                deletedFollowers={parent_deleted_followers}
                following={parent_following}
                messages={unread_parent_messages}
                profileViews={parent_profile_views}
                refetch={refetch}
                conversations={parentConversations}
            />
        );
    }

    return (
        <DashboardLayout pageTitle="Recruiting Hub">
            <NeedsOnboardingInformation> {content}</NeedsOnboardingInformation>
        </DashboardLayout>
    );
}
export const getStaticProps: GetStaticProps = async (ctx) => {
    const contentfulService = container.resolve(ContentfulService);
    const response = await contentfulService.getRecentPosts(2);
    if (response) {
        return {
            props: {
                articles: response.postCollection.items,
            },
            revalidate: 600,
        };
    }

    return {
        props: {
            articles: [],
        },
        revalidate: 600,
    };
};
